import { Paywit } from '@paywit/sdk';

// ============================================================================
// Helpers
// ============================================================================

function validatePublishableKey(publishableKey: string): void {
  if (!publishableKey) {
    throw new Error('Paywit: publishableKey is required');
  }

  // Warn if using a secret key in frontend code
  if (publishableKey.startsWith('sk_')) {
    console.error(
      '[Paywit] WARNING: You are using a secret key (sk_) in frontend code. ' +
        'This is a security risk! Use a publishable key (pk_) instead. ' +
        'See: https://docs.paywit.xyz/security/api-keys'
    );
  }
}

// ============================================================================
// Types
// ============================================================================

export interface PaywitPaymentOptions {
  amount: number;
  description?: string;
  returnUrl?: string;
  clientReferenceId?: string;
}

export interface AttachPaywitButtonOptions {
  publishableKey: string;
  payment: PaywitPaymentOptions;
  /** Override the API origin, e.g. 'https://api.sandbox.paywit.xyz' for testnet. */
  apiBaseUrl?: string;
  onSuccess?: (paymentUrl: string) => void;
  onError?: (error: Error) => void;
}

// ============================================================================
// Core: attachPaywitButton (programmatic API)
// ============================================================================

export function attachPaywitButton(
  target: string | HTMLElement,
  options: AttachPaywitButtonOptions
): void {
  validatePublishableKey(options?.publishableKey);

  if (!options?.payment?.amount || options.payment.amount <= 0) {
    throw new Error('Paywit: payment.amount must be a positive number');
  }

  const element =
    typeof target === 'string'
      ? document.querySelector<HTMLElement>(target)
      : target;

  if (!element) {
    throw new Error('Paywit: target element not found');
  }

  const paywit = new Paywit({
    publishableKey: options.publishableKey,
    apiBaseUrl: options.apiBaseUrl,
  });

  element.addEventListener('click', async (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Disable button during processing
    const wasDisabled = element.hasAttribute('disabled');
    element.setAttribute('disabled', 'true');
    const originalText = element.textContent;
    element.textContent = 'Processing...';

    try {
      const { paymentCheckoutUrl } = await paywit.createPayment(
        options.payment
      );

      options.onSuccess?.(paymentCheckoutUrl);
      paywit.openCheckout(paymentCheckoutUrl);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('[Paywit] Error:', error);

      if (options.onError) {
        options.onError(error);
      } else {
        alert('Payment could not be initiated. Please try again.');
      }
    } finally {
      // Restore button state
      if (!wasDisabled) {
        element.removeAttribute('disabled');
      }
      element.textContent = originalText;
    }
  });
}

// ============================================================================
// Web Component: <paywit-button>
// ============================================================================

const PAYWIT_LOGO_SVG = `<svg class="logo" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="1024" height="1024" fill="#FDFDFD" rx="128"/>
  <path d="M0 0 C0.86429803 -0.00367669 1.72859607 -0.00735338 2.61908495 -0.01114148 C5.52166688 -0.02217276 8.42421299 -0.02602153 11.32681274 -0.02983093 C13.40678012 -0.03609696 15.48674639 -0.04274388 17.56671143 -0.04974365 C24.41333231 -0.07080369 31.25995341 -0.08116102 38.1065979 -0.09111023 C40.47217444 -0.09515975 42.83775087 -0.09927673 45.20332718 -0.10346031 C56.3402098 -0.12253133 67.47708579 -0.13674755 78.6139816 -0.14507228 C91.40613481 -0.1547944 104.19811265 -0.18097313 116.99020422 -0.22157317 C126.90999366 -0.25195722 136.82973358 -0.26656281 146.74956858 -0.26985329 C152.656025 -0.27218232 158.56229458 -0.28085495 164.46870041 -0.30631447 C225.34661753 -0.55495249 283.59783227 7.24204184 329.5206604 51.14717102 C330.26444946 51.82779602 331.00823853 52.50842102 331.77456665 53.20967102 C346.73575413 67.36425535 356.19241389 85.45781375 361.5206604 105.14717102 C361.70024292 105.78243713 361.87982544 106.41770325 362.06484985 107.07221985 C366.05224759 125.2237428 360.38549557 143.45673455 351.5206604 159.14717102 C349.10410231 162.74822314 346.43936427 165.94601194 343.5206604 169.14717102 C342.45525024 170.38853821 342.45525024 170.38853821 341.36831665 171.65498352 C317.50264224 199.08582897 281.81720359 217.15476666 248.50991821 230.34638977 C246.57909178 231.12364931 244.66978052 231.93820582 242.7628479 232.77217102 C241.67230103 233.24654602 240.58175415 233.72092102 239.4581604 234.20967102 C238.53905884 234.61314758 237.61995728 235.01662415 236.67300415 235.43232727 C234.5206604 236.14717102 234.5206604 236.14717102 232.5206604 235.14717102 C234.78681464 233.16241506 237.05820193 231.18429014 239.3331604 229.20967102 C239.97575806 228.64570618 240.61835571 228.08174133 241.28042603 227.50068665 C241.90239868 226.96250305 242.52437134 226.42431946 243.16519165 225.86982727 C244.02141113 225.12358093 244.02141113 225.12358093 244.89492798 224.36225891 C246.5206604 223.14717102 246.5206604 223.14717102 249.5206604 222.14717102 C249.8506604 221.15717102 250.1806604 220.16717102 250.5206604 219.14717102 C252.15596943 217.4497259 253.82763881 215.78705926 255.5206604 214.14717102 C271.5460605 196.60550142 281.96239686 175.69553041 281.7706604 151.58467102 C281.77839478 150.60820618 281.78612915 149.63174133 281.7940979 148.62568665 C281.75351216 133.45676466 276.55895247 120.33313403 265.8800354 109.38545227 C265.10144165 108.64681946 264.3228479 107.90818665 263.5206604 107.14717102 C262.78331665 106.43947571 262.0459729 105.7317804 261.2862854 105.00263977 C243.92805464 89.77050103 218.43566036 85.86065476 196.20523071 85.82832336 C194.68189343 85.81889128 193.15856122 85.80860917 191.63523483 85.79755592 C187.59167223 85.77052357 183.54814544 85.7549763 179.5045166 85.74197388 C175.69653651 85.7279411 171.88865177 85.70332926 168.08071899 85.67964172 C156.97742083 85.61663507 145.87403256 85.57040679 134.7706604 85.52217102 C109.9381604 85.39842102 85.1056604 85.27467102 59.5206604 85.14717102 C59.5206604 149.49717102 59.5206604 213.84717102 59.5206604 280.14717102 C43.82244059 281.40302861 28.63136621 282.47334312 12.95083618 282.40791321 C9.86063596 282.39704787 6.77100122 282.40797768 3.68081665 282.42060852 C-7.23236531 282.42799889 -17.71048854 281.95062316 -28.4793396 280.14717102 C-30.81224382 279.81084761 -33.14553048 279.47715672 -35.4793396 279.14717102 C-35.54779478 247.05265216 -35.60087761 214.95815409 -35.63253559 182.86357927 C-35.63628765 179.07350687 -35.64017063 175.28343461 -35.64413452 171.49336243 C-35.64492145 170.73883537 -35.64570838 169.98430832 -35.64651916 169.20691681 C-35.65956888 156.99379707 -35.68322033 144.78071975 -35.71079799 132.56762516 C-35.73886427 120.03349163 -35.75551844 107.4993803 -35.76160508 94.96521664 C-35.76571899 87.23254423 -35.77870694 79.49999171 -35.803089 71.76735622 C-35.82083907 65.83542536 -35.82270212 59.90355585 -35.81866264 53.97159767 C-35.81948806 51.53998896 -35.82545656 49.10837571 -35.83695602 46.67679405 C-35.85189188 43.35571785 -35.8482888 40.03515372 -35.84010315 36.71406555 C-35.84884167 35.75295836 -35.8575802 34.79185118 -35.86658353 33.80161953 C-35.80586714 24.68139803 -33.27446316 17.02773108 -26.72543335 10.36201477 C-18.48590715 2.76746127 -11.115558 -0.00253522 0 0 Z" fill="#424241" transform="translate(393.4793395996094,283.8528289794922)"/>
  <path d="M0 0 C0.66 0 1.32 0 2 0 C15.05336653 25.251294 20.57414257 50.39604624 12.31591797 78.3125 C6.38701964 96.90576981 -2.73593954 112.89714409 -15 128 C-15.49935059 128.61504395 -15.99870117 129.23008789 -16.51318359 129.86376953 C-26.61367317 142.18248327 -37.50865145 153.10119549 -50 163 C-51.70847566 164.43733084 -53.41680599 165.87483445 -55.125 167.3125 C-115.8581324 216.86247771 -192.1328013 241.65253772 -268 256 C-268.95213379 256.18191895 -269.90426758 256.36383789 -270.88525391 256.55126953 C-287.0811596 259.58616029 -303.59794456 260.63034836 -320 262 C-319.99920441 262.54884857 -319.99840881 263.09769714 -319.99758911 263.66317749 C-319.99511568 269.47326645 -320.03507373 275.28205314 -320.09765625 281.09179688 C-320.11561651 283.2483106 -320.12342179 285.40493745 -320.12011719 287.56152344 C-320.10264784 306.34107616 -322.38942636 322.65269002 -334 338 C-334.44730469 338.62777344 -334.89460937 339.25554687 -335.35546875 339.90234375 C-347.23724467 355.26851143 -366.59651181 362.10636978 -385 365 C-394.9 365 -404.8 365 -415 365 C-415 330.35 -415 295.7 -415 260 C-391.24 259.505 -391.24 259.505 -367 259 C-348.785216 257.32308338 -330.79347284 255.51015652 -312.9296875 251.62451172 C-310.07856978 251.01674846 -307.22174896 250.44351427 -304.36190796 249.87840271 C-287.30011591 246.50047022 -270.6967598 242.48778473 -254.15844727 237.02368164 C-251.23189446 236.07515911 -248.29274779 235.17809694 -245.34796143 234.28819275 C-236.68500339 231.65139751 -228.26678003 228.53466013 -219.85864258 225.1784668 C-217.64617615 224.29601786 -215.42983715 223.42385939 -213.21289062 222.55273438 C-211.79143445 221.99016972 -210.37020142 221.42704085 -208.94921875 220.86328125 C-208.29306076 220.60492996 -207.63690277 220.34657867 -206.96086121 220.08039856 C-204.06109282 218.9183279 -201.61339754 217.74226503 -199 216 C-199.66418945 216.19634033 -200.32837891 216.39268066 -201.01269531 216.5949707 C-204.09104954 217.50336816 -207.17051415 218.40794653 -210.25 219.3125 C-211.29414062 219.62123047 -212.33828125 219.92996094 -213.4140625 220.24804688 C-225.1710762 223.69531941 -237.0037187 226.51757789 -249 229 C-250.29244629 229.26780273 -251.58489258 229.53560547 -252.91650391 229.81152344 C-277.46770681 234.84615712 -302.06762364 238.4717833 -327 241 C-328.30356445 241.13277344 -329.60712891 241.26554688 -330.95019531 241.40234375 C-381.47051252 246.25403863 -456.30727508 251.37353613 -498.8125 217.75 C-500.21628544 216.50893709 -501.61342774 215.26026507 -503 214 C-504.5778125 212.8553125 -504.5778125 212.8553125 -506.1875 211.6875 C-514.13714648 204.09117115 -519.96774304 193.30262534 -520.47070312 182.25756836 C-520.71575849 164.94236444 -512.93818099 150.98908993 -502 138 C-501.360625 137.23816406 -500.72125 136.47632813 -500.0625 135.69140625 C-495.71578194 130.65702381 -491.14819117 126.20364701 -486 122 C-485.00355469 121.17757812 -484.00710938 120.35515625 -482.98046875 119.5078125 C-474.09822085 112.29033679 -464.72785168 106.01556495 -455 100 C-454.37657715 99.61199219 -453.7531543 99.22398437 -453.11083984 98.82421875 C-445.92288502 94.37668398 -445.92288502 94.37668398 -443 93 C-442.34 93.33 -441.68 93.66 -441 94 C-441.61746094 94.59425781 -442.23492187 95.18851562 -442.87109375 95.80078125 C-449.06785418 101.86549503 -454.69497127 108.13117549 -460 115 C-460.6290625 115.81210937 -461.258125 116.62421875 -461.90625 117.4609375 C-471.67909801 130.70571835 -475.72482871 143.72506025 -474 160 C-472.86768273 163.56906403 -471.37612117 166.13614869 -469 169 C-468.12601562 170.21042969 -468.12601562 170.21042969 -467.234375 171.4453125 C-464.64897192 174.40135029 -461.79978486 176.43695642 -458.5 178.5 C-457.87834961 178.8909082 -457.25669922 179.28181641 -456.61621094 179.68457031 C-392.08886718 219.31009075 -284.47493958 193.65338815 -215.78515625 177.453125 C-197.26548777 172.89731776 -179.1189643 166.91974289 -161 161 C-160.26869873 160.76522949 -159.53739746 160.53045898 -158.78393555 160.28857422 C-111.54128931 144.9214714 -64.00951294 122.81417641 -28 88 C-27.26394531 87.31550781 -26.52789063 86.63101563 -25.76953125 85.92578125 C-10.04021915 70.94996913 2.30557505 49.98586181 3.40625 27.94140625 C3.4556003 25.81544725 3.48694603 23.68899164 3.5 21.5625 C3.50644531 20.56798828 3.51289062 19.57347656 3.51953125 18.54882812 C3.40452068 11.93572032 2.23080602 6.22084476 0 0 Z" fill="#434342" transform="translate(773,387)"/>
</svg>`;

const BUTTON_STYLES = `
  :host {
    display: inline-block;
  }

  /* Theme via CSS custom properties set on the <paywit-button> element, e.g.
     paywit-button { --paywit-button-bg: #6366f1; --paywit-button-radius: 12px; } */
  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: var(--paywit-button-padding, 12px 24px);
    font-size: var(--paywit-button-font-size, 16px);
    font-weight: var(--paywit-button-font-weight, 500);
    font-family: var(--paywit-button-font-family, system-ui, -apple-system, BlinkMacSystemFont, sans-serif);
    border-radius: var(--paywit-button-radius, 8px);
    border: var(--paywit-button-border, none);
    background: var(--paywit-button-bg, #000);
    color: var(--paywit-button-color, #fff);
    cursor: pointer;
    transition: opacity 0.15s ease, transform 0.1s ease;
    min-width: var(--paywit-button-min-width, 140px);
  }

  button:hover:not(:disabled) {
    opacity: var(--paywit-button-hover-opacity, 0.9);
  }

  button:active:not(:disabled) {
    transform: scale(0.98);
  }

  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .logo {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

class PaywitButtonElement extends HTMLElement {
  private shadow: ShadowRoot;
  private button: HTMLButtonElement;
  private paywit: Paywit | null = null;
  private isProcessing = false;

  static get observedAttributes() {
    return [
      'amount',
      'publishable-key',
      'api-base-url',
      'label',
      'description',
      'return-url',
      'client-reference-id',
      'disabled',
    ];
  }

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });

    // Create styles
    const style = document.createElement('style');
    style.textContent = BUTTON_STYLES;

    // Create button
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.setAttribute('part', 'button');

    this.shadow.appendChild(style);
    this.shadow.appendChild(this.button);
  }

  connectedCallback() {
    this.updateButtonContent();
    this.initPaywit();
    this.button.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.button.removeEventListener('click', this.handleClick);
  }

  attributeChangedCallback() {
    this.initPaywit();
    this.updateButtonContent();
  }

  private updateButtonContent() {
    if (this.isProcessing) return;

    // Priority: slot content → `label` attribute → neutral default.
    // (No hardcoded currency symbol — set your own copy via the label or slot.)
    const slotContent = this.textContent?.trim();
    const label = this.getAttribute('label')?.trim();
    const text = slotContent || label || 'Pay with Paywit';

    // Logo is a trusted constant (innerHTML); the merchant-supplied label goes through
    // textContent so it can never be interpreted as HTML.
    this.button.innerHTML = `${PAYWIT_LOGO_SVG}<span class="text"></span>`;
    const textNode = this.button.querySelector('.text');
    if (textNode) textNode.textContent = text;
  }

  private initPaywit() {
    const publishableKey = this.getAttribute('publishable-key');
    if (publishableKey) {
      this.paywit = new Paywit({
        publishableKey,
        apiBaseUrl: this.getAttribute('api-base-url') ?? undefined,
      });
    }
  }

  private handleClick = async (event: Event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.isProcessing || this.hasAttribute('disabled')) return;

    const publishableKey = this.getAttribute('publishable-key');
    const amount = parseFloat(this.getAttribute('amount') || '0');

    if (!publishableKey) {
      const error = new Error('publishable-key attribute is required');
      console.error('[Paywit]', error.message);
      this.dispatchEvent(
        new CustomEvent('paywit-error', { detail: { error } })
      );
      return;
    }

    // Warn if using secret key in frontend
    if (publishableKey.startsWith('sk_')) {
      console.error(
        '[Paywit] WARNING: You are using a secret key (sk_) in frontend code. ' +
          'This is a security risk! Use a publishable key (pk_) instead. ' +
          'See: https://docs.paywit.xyz/security/api-keys'
      );
    }

    if (!amount || amount <= 0) {
      console.error('[Paywit] amount must be a positive number');
      this.dispatchEvent(
        new CustomEvent('paywit-error', {
          detail: { error: new Error('amount must be a positive number') },
        })
      );
      return;
    }

    this.isProcessing = true;
    const originalContent = this.button.textContent;
    this.button.disabled = true;
    this.button.innerHTML = '<span class="spinner"></span> Processing...';

    try {
      if (!this.paywit) {
        this.paywit = new Paywit({
          publishableKey,
          apiBaseUrl: this.getAttribute('api-base-url') ?? undefined,
        });
      }

      const payment: PaywitPaymentOptions = {
        amount,
        description: this.getAttribute('description') || undefined,
        returnUrl: this.getAttribute('return-url') || undefined,
        clientReferenceId:
          this.getAttribute('client-reference-id') || undefined,
      };

      const { paymentCheckoutUrl } = await this.paywit.createPayment(payment);

      this.dispatchEvent(
        new CustomEvent('paywit-success', {
          detail: { paymentCheckoutUrl },
        })
      );

      this.paywit.openCheckout(paymentCheckoutUrl);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('[Paywit] Error:', error);

      this.dispatchEvent(
        new CustomEvent('paywit-error', {
          detail: { error },
        })
      );

      // Default error handling if no listener
      if (!this.hasAttribute('no-default-error')) {
        alert('Payment could not be initiated. Please try again.');
      }
    } finally {
      this.isProcessing = false;
      this.button.disabled = false;
      this.button.textContent = originalContent;
    }
  };
}

// Register the custom element
if (typeof window !== 'undefined' && !customElements.get('paywit-button')) {
  customElements.define('paywit-button', PaywitButtonElement);
}

// ============================================================================
// Auto-initialization: data-paywit-* attributes
// ============================================================================

function initDataAttributes() {
  // Find all elements with data-paywit-amount (the required attribute)
  const elements = document.querySelectorAll<HTMLElement>(
    '[data-paywit-amount]'
  );

  elements.forEach((element) => {
    // Skip if already initialized
    if (element.hasAttribute('data-paywit-initialized')) return;

    const publishableKey = element.getAttribute('data-paywit-publishable-key');
    const apiBaseUrl =
      element.getAttribute('data-paywit-api-base-url') || undefined;
    const amount = parseFloat(
      element.getAttribute('data-paywit-amount') || '0'
    );
    const description =
      element.getAttribute('data-paywit-description') || undefined;
    const returnUrl =
      element.getAttribute('data-paywit-return-url') || undefined;
    const clientReferenceId =
      element.getAttribute('data-paywit-client-reference-id') || undefined;

    if (!publishableKey) {
      console.error(
        '[Paywit] data-paywit-publishable-key is required on element:',
        element
      );
      return;
    }

    if (!amount || amount <= 0) {
      console.error(
        '[Paywit] data-paywit-amount must be a positive number:',
        element
      );
      return;
    }

    attachPaywitButton(element, {
      publishableKey,
      apiBaseUrl,
      payment: {
        amount,
        description,
        returnUrl,
        clientReferenceId,
      },
    });

    element.setAttribute('data-paywit-initialized', 'true');
  });
}

// Auto-init when DOM is ready
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDataAttributes);
  } else {
    // DOM already loaded, init immediately
    initDataAttributes();
  }

  // Also observe for dynamically added elements
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        initDataAttributes();
        break;
      }
    }
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
}

// ============================================================================
// Exports
// ============================================================================

export { PaywitButtonElement };
